/**
 * Build Your Beer Club — bundle builder + Loop Subscriptions connector.
 *
 * WHAT CHANGED (fix for "bundle discount not applied on recurring orders")
 * ------------------------------------------------------------------------
 * Per Loop's Bundle API docs, a custom BYOB (type: RANGE) page must:
 *   1. Create a bundle transaction:
 *        POST https://api.loopsubscriptions.com/storefront/2026-04/bundle/{bundleId}/transaction
 *      -> returns a txnId.
 *   2. Add EVERY bundle line in ONE /cart/add.js call with the line-item
 *      property   _bundleId: <txnId>   (key must be exactly "_bundleId").
 *   3. Apply the tier discount code via /discount/{code} (BYOB only).
 *
 * The old version skipped steps 1 and 2 (it sent _loop_bundle_id, which is the
 * wrong key and the wrong value), so Loop never linked the lines into a bundle.
 * The discount code only discounted the first checkout; renewals had no bundle
 * to carry the discount.
 *
 * IDs the transaction needs:
 *   - bundleId   : the public Bundle ID (`id` from List/Read bundle details)
 *   - boxSizeId  : boxSizes[].id
 *   - discountId : discounts[].id for the tier being purchased
 * They are looked up automatically from Loop at click time (matching each tier
 * by its discount code), or you can pin them in the section settings.
 *
 * CARD INTERACTION
 * Each card starts with ONE "Add to Box" button; clicking it adds one step and
 * swaps in a live stepper (-, qty, +). Dropping to 0 removes the item.
 *
 * QUANTITY STEP
 * Products sold in multiples (e.g. 12-pack = 2 units of a 6-pack variant) use
 * data-pack-multiplier as the step. The quantity shown is the real quantity
 * sent to Shopify and to Loop.
 *
 * SINGLE CTA, TWO MODES
 *   - below MIN_PACKS: disabled
 *   - MIN_PACKS .. (first tier - 1): "Add To Cart" at full price (no subscription)
 *   - first tier and up: frequency modal -> subscription bundle flow
 */
(function () {
  const root = document.querySelector('[data-mak]');
  if (!root) return;

  const LOOP_API = 'https://api.loopsubscriptions.com/storefront/2026-04';

  const MIN_PACKS = parseInt(root.dataset.minPacks, 10) || 2;
  const TIERS = JSON.parse(root.dataset.tiers || '[]')
    .filter((t) => t.qty > 0)
    .sort((a, b) => a.qty - b.qty);
  const FREQUENCIES = JSON.parse(root.dataset.frequencies || '[]');
  const FREQUENCY_PLAN_MAP = parsePlanMap(root.dataset.frequencyPlans || '');
  const LOOP_BUNDLE_ID = (root.dataset.loopBundleId || '').trim();
  const BOX_SIZE_ID_OVERRIDE = (root.dataset.boxSizeId || '').trim();
  const SHOP_DOMAIN =
    (root.dataset.myshopifyDomain || '').trim() ||
    (window.Shopify && window.Shopify.shop) ||
    '';
  const CART_URL = root.dataset.cartUrl || '/cart';

  const els = {
    tierStrip: root.querySelector('[data-mak-tier-strip]'),
    lineItems: root.querySelector('[data-mak-line-items]'),
    emptyBox: root.querySelector('[data-mak-empty-box]'),
    emptyNote: root.querySelector('[data-mak-empty-note]'),
    selectedCount: root.querySelector('[data-mak-selected-count]'),
    itemCount: root.querySelector('[data-mak-item-count]'),
    subtotal: root.querySelector('[data-mak-subtotal]'),
    discountRow: root.querySelector('[data-mak-discount-row]'),
    discountPct: root.querySelector('[data-mak-discount-pct]'),
    discountAmt: root.querySelector('[data-mak-discount-amt]'),
    finalPrice: root.querySelector('[data-mak-final-price]'),
    startBtn: root.querySelector('[data-mak-start-btn]'),
    minNote: root.querySelector('[data-mak-min-note]'),
    tierTrack: root.querySelector('[data-mak-tier-track]'),
    discountTabs: root.querySelector('[data-mak-discount-tabs]'),
    nextDiscount: root.querySelector('[data-mak-next-discount]'),
    maxQty: root.querySelector('[data-mak-max-qty]'),
    modal: root.querySelector('[data-mak-modal]'),
    modalClose: root.querySelector('[data-mak-modal-close]'),
    freqOptions: root.querySelector('[data-mak-freq-options]'),
    confirmBtn: root.querySelector('[data-mak-confirm-btn]'),
    modalError: root.querySelector('[data-mak-modal-error]'),
  };

  const SUBSCRIPTION_LABEL = els.startBtn ? els.startBtn.textContent.trim() : 'Continue - Choose Delivery Frequency';
  const FULL_PRICE_LABEL = 'Add To Cart';
  const CONFIRM_LABEL = els.confirmBtn ? els.confirmBtn.textContent.trim() : 'Continue to Checkout';

  /** variantId -> {variantId, title, price, image, packSize, qty} */
  const selected = new Map();
  let chosenFrequency = null;

  const FREQUENCY_LABELS = {
    2: 'Every 2 weeks',
    4: 'Every 4 weeks - 1 month',
    8: 'Every 8 weeks - 2 months',
    12: 'Every 12 weeks - 3 months',
    16: 'Every 16 weeks - 4 months',
  };

  function frequencyLabel(weeks) {
    return FREQUENCY_LABELS[weeks] || `Every ${weeks} weeks`;
  }

  if (els.emptyNote && TIERS.length) {
    els.emptyNote.textContent = `Add ${TIERS[0].qty} items to begin and unlock ${TIERS[0].discount}% off.`;
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function totalQty() {
    let n = 0;
    selected.forEach((i) => (n += i.qty));
    return n;
  }

  function currentTier() {
    const count = totalQty();
    let tier = null;
    for (const t of TIERS) {
      if (count >= t.qty) tier = t;
    }
    return tier;
  }

  function nextTierTarget(count) {
    for (const t of TIERS) {
      if (count < t.qty) return t.qty;
    }
    return TIERS.length ? TIERS[TIERS.length - 1].qty : 0;
  }

  function renderNextDiscountMessage() {
    if (!els.nextDiscount || !TIERS.length) return;

    const count = totalQty();
    const nextTier = TIERS.find((tier) => count < tier.qty);

    if (nextTier) {
      const remaining = nextTier.qty - count;
      const itemText = remaining === 1 ? 'item' : 'items';

      if (count === 0) {
        els.nextDiscount.innerHTML = `
          Select <strong>${nextTier.qty} items</strong>
          to get <strong>${nextTier.discount}% off</strong>.
        `;
        return;
      }

      els.nextDiscount.innerHTML = `
        Add <strong>${remaining} more ${itemText}</strong>
        to get <strong>${nextTier.discount}% off</strong>
        <span>(total ${nextTier.qty})</span>.
      `;
      return;
    }

    const highestTier = TIERS[TIERS.length - 1];
    els.nextDiscount.innerHTML = `
      You’ve unlocked the maximum
      <strong>${highestTier.discount}% discount</strong>.
    `;
  }

  function renderTierStrip() {
    const active = currentTier();
    const count = totalQty();
    els.tierStrip.innerHTML = TIERS.map((t) => {
      const isActive = active && active.qty === t.qty;
      const reached = count >= t.qty;
      const cls = ['mak-bybc-tier-pill'];
      if (isActive) cls.push('active');
      else if (reached) cls.push('reached');
      return `<div class="${cls.join(' ')}">
        <span><span class="num">${t.qty}</span>Pick ${t.qty} items</span>
        <strong>SAVE ${t.discount}%</strong>
      </div>`;
    }).join('');
  }

  function renderProgress() {
    if (!TIERS.length) return;
    const count = totalQty();

    els.tierTrack.innerHTML = TIERS.map((t, i) => {
      const reached = count >= t.qty;
      const nodeHtml = `<span class="mak-bybc-tier-node ${reached ? 'reached' : ''}">${t.qty}</span>`;
      if (i === TIERS.length - 1) return nodeHtml;
      return `${nodeHtml}<span class="mak-bybc-tier-connector ${reached ? 'reached' : ''}"></span>`;
    }).join('');

    const active = currentTier();
    els.discountTabs.innerHTML = TIERS.map((t) => {
      const isActive = active && active.qty === t.qty;
      return `<span class="mak-bybc-discount-tab ${isActive ? 'active' : ''}">${t.discount}% OFF</span>`;
    }).join('');
  }

  function renderLineItems() {
    const items = Array.from(selected.values());

    if (els.emptyBox) els.emptyBox.hidden = items.length > 0;
    if (els.lineItems) els.lineItems.hidden = items.length === 0;

    els.lineItems.innerHTML = items.map((item) => `
      <li class="mak-bybc-line-item" data-variant-id="${item.variantId}">
        <img src="${item.image}" alt="">
        <div class="mak-bybc-line-item-info">
          <strong>${item.title}</strong>
          <span>Qty ${item.qty}</span>
          <span>${money(item.price * item.qty)}</span>
        </div>
        <button type="button" class="mak-bybc-line-item-remove" data-mak-remove="${item.variantId}" aria-label="Remove">&times;</button>
      </li>
    `).join('');
  }

  function renderTotals() {
    let subtotal = 0;
    selected.forEach((i) => (subtotal += i.price * i.qty));
    const tier = currentTier();
    const discountPct = tier ? tier.discount : 0;
    const discountAmt = Math.round(subtotal * (discountPct / 100));
    const finalPrice = subtotal - discountAmt;
    const count = totalQty();

    els.selectedCount.textContent = count;
    els.itemCount.textContent = count;
    els.subtotal.textContent = money(subtotal);
    if (els.maxQty) els.maxQty.textContent = nextTierTarget(count);

    if (discountPct > 0) {
      els.discountRow.hidden = false;
      els.discountPct.textContent = discountPct;
      els.discountAmt.textContent = '-' + money(discountAmt);
    } else {
      els.discountRow.hidden = true;
    }
    els.finalPrice.textContent = money(finalPrice);
  }

  function renderCta() {
    const count = totalQty();
    const firstTierQty = TIERS.length ? TIERS[0].qty : Infinity;
    const eligibleForSubscription = count >= firstTierQty;
    const eligibleForFullPrice = count >= MIN_PACKS && !eligibleForSubscription;

    if (eligibleForSubscription) {
      els.startBtn.disabled = false;
      els.startBtn.textContent = SUBSCRIPTION_LABEL;
      els.startBtn.dataset.makMode = 'subscription';
      els.minNote.textContent = '';
    } else if (eligibleForFullPrice) {
      els.startBtn.disabled = false;
      els.startBtn.textContent = FULL_PRICE_LABEL;
      els.startBtn.dataset.makMode = 'full-price';
      els.minNote.textContent = '';
    } else {
      els.startBtn.disabled = true;
      els.startBtn.textContent = SUBSCRIPTION_LABEL;
      els.startBtn.dataset.makMode = 'subscription';
      const remaining = MIN_PACKS - count;
      els.minNote.textContent = `Add ${remaining} more item${remaining === 1 ? '' : 's'} to continue (minimum ${MIN_PACKS}).`;
    }
  }

  function syncCardStates() {
    root.querySelectorAll('[data-mak-card]').forEach((card) => {
      const variantId = card.dataset.variantId;
      const item = selected.get(variantId);
      const inBox = !!item;

      const stepper = card.querySelector('[data-mak-stepper]');
      const addBtn = card.querySelector('[data-mak-add]');
      const qtyEl = card.querySelector('[data-mak-qty]');

      card.classList.toggle('mak-bybc-card--in-cart', inBox);
      if (qtyEl) qtyEl.textContent = inBox ? item.qty : 0;
      if (stepper) stepper.hidden = !inBox;
      if (addBtn) addBtn.hidden = inBox;
    });
  }

  function render() {
    renderTierStrip();
    renderProgress();
    renderLineItems();
    renderTotals();
    renderNextDiscountMessage();
    renderCta();
    syncCardStates();
  }

  function addToBox(card) {
    const variantId = card.dataset.variantId;
    if (!variantId) {
      console.warn('This card has no product/variant selected in the theme editor yet.');
      return;
    }
    const step = parseInt(card.dataset.packMultiplier, 10) || 1;
    selected.set(variantId, {
      variantId,
      title: card.dataset.title,
      price: parseInt(card.dataset.price, 10) || 0,
      image: card.dataset.image,
      packSize: card.dataset.packSize,
      packMultiplier: step,
      frequencyPlans: parsePlanMap(card.dataset.frequencyPlans || ''),
      qty: step,
    });
    render();
  }

  function setQty(card, qty) {
    const variantId = card.dataset.variantId;
    if (!variantId) {
      console.warn('This card has no product/variant selected in the theme editor yet.');
      return;
    }
    qty = Math.max(0, qty);
    const existing = selected.get(variantId);

    if (qty === 0) {
      selected.delete(variantId);
    } else if (existing) {
      selected.set(variantId, { ...existing, qty });
    }
    render();
  }

  root.addEventListener('click', (e) => {
    const increaseBtn = e.target.closest('[data-mak-increase]');
    if (increaseBtn) {
      const card = increaseBtn.closest('[data-mak-card]');
      const item = selected.get(card.dataset.variantId);
      const step = parseInt(card.dataset.packMultiplier, 10) || 1;
      setQty(card, (item ? item.qty : 0) + step);
      return;
    }
    const decreaseBtn = e.target.closest('[data-mak-decrease]');
    if (decreaseBtn) {
      const card = decreaseBtn.closest('[data-mak-card]');
      const item = selected.get(card.dataset.variantId);
      const step = parseInt(card.dataset.packMultiplier, 10) || 1;
      setQty(card, (item ? item.qty : 0) - step);
      return;
    }
    const addBtn = e.target.closest('[data-mak-add]');
    if (addBtn) {
      const card = addBtn.closest('[data-mak-card]');
      addToBox(card);
      return;
    }
    const removeBtn = e.target.closest('[data-mak-remove]');
    if (removeBtn) {
      selected.delete(removeBtn.dataset.makRemove);
      render();
      return;
    }
    if (e.target.closest('[data-mak-start-btn]') && !els.startBtn.disabled) {
      if (els.startBtn.dataset.makMode === 'full-price') {
        addFullPriceToCart();
      } else {
        openFrequencyModal();
      }
      return;
    }
    if (e.target.closest('[data-mak-modal-close]')) {
      closeFrequencyModal();
      return;
    }
    const freqOpt = e.target.closest('[data-mak-freq-option]');
    if (freqOpt) {
      chosenFrequency = freqOpt.dataset.makFreqOption;
      els.freqOptions.querySelectorAll('[data-mak-freq-option]').forEach((el) =>
        el.classList.toggle('selected', el === freqOpt)
      );
      els.confirmBtn.disabled = false;
      return;
    }
    if (e.target.closest('[data-mak-confirm-btn]')) {
      confirmSubscription();
      return;
    }
  });

  function openFrequencyModal() {
    chosenFrequency = null;
    els.confirmBtn.disabled = true;
    els.modalError.hidden = true;
    els.freqOptions.innerHTML = FREQUENCIES.map((weeks) => `
      <div class="mak-bybc-freq-option" data-mak-freq-option="${weeks}">${frequencyLabel(weeks)}</div>
    `).join('');
    els.modal.hidden = false;
  }

  function closeFrequencyModal() {
    els.modal.hidden = true;
  }

  function parsePlanMap(str) {
    const map = {};
    (str || '').split(',').forEach((pair) => {
      const [freq, id] = pair.split(':').map((s) => (s || '').trim());
      if (freq && id) map[freq] = id;
    });
    return map;
  }

  /* ------------------------------------------------------------------ *
   * Loop Bundle API helpers
   * ------------------------------------------------------------------ */

  // Loop wraps every response as { success, message, data, code }.
  async function loopRequest(path, options) {
    const res = await fetch(LOOP_API + path, options);
    const json = await res.json().catch(() => null);
    if (!res.ok || !json || json.success === false) {
      const code = (json && json.code) || res.status;
      const msg = (json && json.message) || 'request failed';
      throw new Error(`Loop API (${path.split('?')[0]}): ${msg} [${code}]`);
    }
    return json.data;
  }

  const domainQuery = () => `?myshopifyDomain=${encodeURIComponent(SHOP_DOMAIN)}`;

  // Resolves { bundleId, details } where details is the "Read bundle details"
  // payload (boxSizes, sellingPlans, products, discounts). Cached after first
  // success so we only look it up once per page view.
  let bundleConfigPromise = null;
  function getBundleConfig() {
    if (!bundleConfigPromise) {
      bundleConfigPromise = loadBundleConfig().catch((err) => {
        bundleConfigPromise = null; // allow retry on next click
        throw err;
      });
    }
    return bundleConfigPromise;
  }

  async function loadBundleConfig() {
    let bundleId = LOOP_BUNDLE_ID;

    if (bundleId) {
      try {
        const details = await loopRequest(`/bundle/${encodeURIComponent(bundleId)}`);
        return { bundleId, details };
      } catch (err) {
        console.warn('[BYBC] Configured Loop bundle ID failed, falling back to List bundles.', err);
      }
    }

    // Fallback: find the active BYOB (RANGE) bundle from List bundles.
    const list = await loopRequest(`/bundle${domainQuery()}`);
    const bundles = Array.isArray(list) ? list : (list && list.bundles) || [];
    const byob = bundles.find((b) => b.type === 'RANGE' && (!b.status || b.status === 'ACTIVE'));
    if (!byob) throw new Error('No active BYOB (RANGE) bundle found in Loop.');
    bundleId = byob.id;
    console.info('[BYBC] Using Loop bundle id from List bundles:', bundleId,
      '— set this in the section setting "Loop bundle ID" to skip the lookup.');
    const details = await loopRequest(`/bundle/${encodeURIComponent(bundleId)}`);
    return { bundleId, details };
  }

  // boxSizes[]: { id, name, minSize, maxSize (0 = no max), defaultSelected }.
  // Pick the size whose range contains the item count (largest minSize wins),
  // then fall back to defaultSelected, then the first one.
  function pickBoxSizeId(details, itemCount) {
    if (BOX_SIZE_ID_OVERRIDE) return BOX_SIZE_ID_OVERRIDE;
    const sizes = (details && details.boxSizes) || [];
    if (!sizes.length) return null;
    const fits = sizes
      .filter((b) => itemCount >= (b.minSize || 0) && (!b.maxSize || itemCount <= b.maxSize))
      .sort((a, b) => (b.minSize || 0) - (a.minSize || 0));
    if (fits.length) return fits[0].id;
    const def = sizes.find((b) => b.defaultSelected);
    return (def || sizes[0]).id;
  }

  function pickDiscountId(details, tier) {
    if (tier.discountId) return tier.discountId;
    const discounts = (details && details.discounts) || [];
    const byCode = discounts.find((d) => d.code && d.code === tier.code);
    if (byCode) return byCode.id;
    // Last resort: match on value + minCartQuantity (real Loop field names).
    const byValue = discounts.find(
      (d) => Number(d.value) === Number(tier.discount) && Number(d.minCartQuantity) === Number(tier.qty)
    );
    return byValue ? byValue.id : null;
  }

  // Sanity checks against Read bundle details so problems surface as a clear
  // message instead of a failed add-to-cart later.
  function validateAgainstBundle(details, resolved) {
    const products = (details && details.products) || [];
    const variants = new Map();
    products.forEach((p) => (p.variants || []).forEach((v) => variants.set(String(v.shopifyId), v)));

    const notInBundle = [];
    const outOfStock = [];
    resolved.forEach(({ item }) => {
      const v = variants.get(String(item.variantId));
      if (!v) notInBundle.push(item.title);
      else if (v.outOfStock) outOfStock.push(item.title);
    });
    if (notInBundle.length) {
      throw new Error(
        `These products aren\u2019t part of the Loop bundle: ${notInBundle.join(', ')}. ` +
        'Add them to the selling plan linked to the bundle in Loop.'
      );
    }
    if (outOfStock.length) {
      throw new Error(`Out of stock: ${outOfStock.join(', ')}. Please remove them from your box.`);
    }

    const plans = ((details && details.sellingPlans) || []).map((p) => String(p.shopifyId));
    resolved.forEach(({ planId }) => {
      if (plans.length && !plans.includes(String(planId))) {
        console.warn('[BYBC] Selling plan', planId, 'is not in the bundle\u2019s sellingPlans:', plans);
      }
    });
  }

  // Step 1 of Loop's flow: create the transaction and return its txnId.
  async function createBundleTransaction(tier, resolved) {
    const { bundleId, details } = await getBundleConfig();

    validateAgainstBundle(details, resolved);

    const boxSizeId = pickBoxSizeId(details, totalQty());
    if (!boxSizeId) {
      throw new Error('Could not determine the Loop box size ID. Set "Box size ID" in the section settings.');
    }
    const discountId = pickDiscountId(details, tier);
    if (!discountId) {
      throw new Error(
        `Could not match the ${tier.discount}% tier to a Loop discount. ` +
        'Check the tier discount code, or set the tier\u2019s "Discount ID" in the section settings.'
      );
    }

    // All lines share one frequency, so one selling plan for the transaction.
    const planIds = Array.from(new Set(resolved.map((r) => String(r.planId))));
    if (planIds.length > 1) {
      console.warn('[BYBC] Items resolved to different selling plans; using the first one for the transaction.', planIds);
    }

    const body = {
      boxSizeId,
      discountId,
      sellingPlanShopifyId: Number(planIds[0]),
      quantity: 1, // number of BUNDLES — never the item count
      productVariants: resolved.map(({ item }) => ({
        shopifyId: Number(item.variantId),
        quantity: item.qty, // child quantity; the box total is the sum of these
      })),
    };

    const data = await loopRequest(`/bundle/${encodeURIComponent(bundleId)}/transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const txnId = data && (data.txnId || data.transactionId || data.id);
    if (!txnId) {
      console.error('[BYBC] Unexpected create-transaction response:', data);
      throw new Error('Loop did not return a transaction ID.');
    }
    return txnId;
  }

  /* ------------------------------------------------------------------ *
   * Cart flows
   * ------------------------------------------------------------------ */

  // Full-price, no-subscription add-to-cart (exactly MIN_PACKS items).
  async function addFullPriceToCart() {
    const itemsList = Array.from(selected.values());
    if (!itemsList.length) return;

    els.startBtn.disabled = true;
    els.startBtn.textContent = 'Adding to cart...';

    const items = itemsList.map((item) => ({
      id: item.variantId,
      quantity: item.qty,
    }));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add items to cart.');
      }

      window.location.href = CART_URL;
    } catch (err) {
      console.error(err.message || 'Something went wrong adding items to cart.');
      renderCta();
    }
  }

  async function confirmSubscription() {
    const tier = currentTier();
    if (!tier || !chosenFrequency) return;

    const itemsList = Array.from(selected.values());

    const missing = [];
    const resolved = itemsList.map((item) => {
      const planId =
        (item.frequencyPlans && item.frequencyPlans[chosenFrequency]) ||
        FREQUENCY_PLAN_MAP[chosenFrequency];
      if (!planId) missing.push(item.title);
      return { item, planId };
    });

    if (missing.length) {
      els.modalError.hidden = false;
      els.modalError.textContent =
        `No selling plan is mapped for "${missing.join('", "')}" at this frequency yet. ` +
        'Check that product is added to the selling plan in Loop, and that its ID is correct ' +
        '(either in this product\u2019s block override or the section\u2019s global map).';
      return;
    }

    els.modalError.hidden = true;
    els.confirmBtn.disabled = true;
    els.confirmBtn.textContent = 'Adding to cart...';

    try {
      // 1) Create the Loop bundle transaction -> txnId
      const txnId = await createBundleTransaction(tier, resolved);

      // 2) Add EVERY bundle line in ONE call, each tagged with _bundleId = txnId
      const items = resolved.map(({ item, planId }) => ({
        id: item.variantId,
        quantity: item.qty,
        selling_plan: planId,
        properties: {
          _bundleId: txnId,
        },
      }));

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add items to cart.');
      }

      // 3) BYOB: apply the tier discount code through Shopify's discount route
      if (tier.code) {
        window.location.href = `/discount/${encodeURIComponent(tier.code)}?redirect=${encodeURIComponent(CART_URL)}`;
      } else {
        window.location.href = CART_URL;
      }
    } catch (err) {
      console.error('[BYBC]', err);
      els.modalError.hidden = false;
      els.modalError.textContent = err.message || 'Something went wrong adding your box to the cart.';
      els.confirmBtn.disabled = false;
      els.confirmBtn.textContent = CONFIRM_LABEL;
    }
  }

  render();
})();
